import {
  NICKNAME_MAX_LENGTH,
  normalizeNicknameCandidate,
} from "@bindery/shared/auth/authConstants";
import { useLogger } from "@bindery/logging/react";
import { Button } from "@bindery/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bindery/ui/components/ui/dialog";
import { Input } from "@bindery/ui/components/ui/input";
import { Label } from "@bindery/ui/components/ui/label";
import { useId, useState } from "react";
import { writeNickname } from "@/storage/nickname";

export type NicknameCheck =
  | { ok: true; nickname: string }
  | { ok: false; reason: string };

export function checkNickname(raw: string): NicknameCheck {
  if (raw.trim() === "") return { ok: false, reason: "Please enter a name" };
  const nickname = normalizeNicknameCandidate(raw);
  return nickname === undefined
    ? {
        ok: false,
        reason: "Letters, numbers, spaces, hyphens, and apostrophes only",
      }
    : { ok: true, nickname };
}

export function NicknameDialog({
  open,
  onOpenChange,
  readerId,
  initialValue,
  save = writeNickname,
  onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readerId: string;
  initialValue: string | null;
  save?: (readerId: string, nickname: string) => boolean;
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const mode = initialValue ? "edit" : "add";
  const [nickname, setNickname] = useState(initialValue ?? "");
  const [error, setError] = useState<string | null>(null);
  const log = useLogger();
  const inputId = useId();
  const errorId = useId();

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setNickname(initialValue ?? "");
      setError(null);
    }
    onOpenChange(next);
  };

  const submit = () => {
    const checked = checkNickname(nickname);
    if (!checked.ok) {
      setError(checked.reason);
      return;
    }
    const saved = save(readerId, checked.nickname);
    log.event("reading.nickname.update", {
      success: saved,
      nickname_length: checked.nickname.length,
    });
    if (!saved) {
      setError("Something went wrong");
      return;
    }
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            {mode === "add"
              ? "Every great adventurer needs a name!"
              : "Update your reading name"}
          </DialogTitle>
          <DialogDescription className="text-center">
            This is how the app will greet you.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor={inputId} className="sr-only">
            Your reading name
          </Label>
          <Input
            id={inputId}
            value={nickname}
            onChange={(event) => {
              setNickname(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              submit();
            }}
            placeholder="Explorer, Champion, Reader…"
            maxLength={NICKNAME_MAX_LENGTH}
            aria-invalid={error !== null}
            aria-describedby={error === null ? undefined : errorId}
            className="text-center"
          />
          {error && (
            <p
              id={errorId}
              role="alert"
              className="text-destructive mt-2 text-center text-sm"
            >
              {error}
            </p>
          )}
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-center">
          <Button onClick={submit} className="w-full sm:w-auto">
            That’s me!
          </Button>
          <Button
            onClick={() => handleOpenChange(false)}
            variant={mode === "add" ? "ghost" : "outline"}
            className="w-full sm:w-auto"
          >
            {mode === "add" ? "Skip for now" : "Cancel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
